import { FormattedMessage, injectIntl } from "react-intl"
import React, { Component, lazy } from "react"
import { getFormValues, propTypes, reduxForm } from "redux-form"
import PropTypes from "prop-types"

import { connect } from "react-redux"
import { notification } from "antd"
import titleize from "titleize"

import { ACTIONS } from "../../globals"
import { bindActions, bindSelectors } from "src/store/utils"
import StepHandler from "../StepHandler"
import actions from "src/store/actions"
import selectors from "src/store/selectors"
import { getCurrentSlug } from "src/store/utils/routeUtils"
import {
  buildLinks,
  buildPdfDocuments,
  buildSupportingDocuments,
  toMediaList,
  toRelatedDocument,
} from "../../containers/DocumentItemPage/Document/DocumentEditor/DocumentForm/helpers"
import FormattedTitle from "../../components/FormattedTitle"

const RelationsStep = lazy(
  () => /* webpackPrefetch: 1 */ import("../steps/opfDocuments/RelationsStep")
)
const DocumentsStep = lazy(() => /* webpackPrefetch: -1 */ import("../steps/DocumentsNewStep"))
const LinksStep = lazy(() => /* webpackPrefetch: -2 */ import("../steps/LinksNewStep"))

export class DocumentForm extends Component {
  getUploadPdfStep = () => {
    const { item } = this.props
    return this.props.formValues
      ? [
          {
            title: "form.step.uploadPdf.title",
            component: DocumentsStep,
            props: {
              fieldName: "pdfDocuments",
              customUploadRequest: this.customUploadRequest,
              cardTitle: (
                <FormattedMessage
                  id="form.step.pdf.title"
                  values={{ type: () => titleize(item.type) }}
                />
              ),
              cardDescription: (
                <FormattedMessage
                  id="form.step.pdf.description"
                  values={{ type: () => titleize(item.type) }}
                />
              ),
              acceptedFileExtensions: ["pdf"],
            },
          },
        ]
      : []
  }

  getEditRelationsStep = () => {
    const { item } = this.props
    return this.props.formValues
      ? [
          {
            title: "form.step.relations.title",
            component: RelationsStep,
            props: {
              item,
              cardTitle: <FormattedMessage id="form.step.relations.intro.title" />,
              cardDescription: <FormattedMessage id="form.step.relations.intro.description" />,
            },
          },
        ]
      : []
  }

  getEditTools = () =>
    this.props.formValues
      ? [
          {
            title: "form.step.editDocuments.title",
            component: DocumentsStep,
            props: {
              fieldName: "supportingDocuments",
              cardTitle: <FormattedTitle id={"form.step.addDocuments.title"} />,
              cardDescription: <FormattedMessage id="form.step.addDocuments.description" />,
              customUploadRequest: this.customUploadRequest,
              multiple: true,
            },
          },
          {
            title: "form.step.editLinks.title",
            component: LinksStep,
            props: { fieldName: "links" },
          },
        ]
      : []

  componentDidMount() {
    const { item, initialize, mode, defaultDocument } = this.props
    if (!item) return

    const original = defaultDocument || item
    const translation = item.translated ? item : undefined

    switch (mode) {
      case ACTIONS.UPLOAD_PDF:
        initialize({
          pdfDocuments: buildPdfDocuments(original, translation),
        })
        break
      case ACTIONS.EDIT_TOOLS:
        initialize({
          links: buildLinks(original, translation),
          supportingDocuments: buildSupportingDocuments(original, translation),
        })
        break
    }
  }

  customUploadRequest = ({ onSuccess, onError, file }) => {
    const { uploadDocumentFile, item } = this.props
    return uploadDocumentFile({ opfDocument: item, file })
      .then((result) => onSuccess(result, result.media))
      .catch(onError)
  }

  render() {
    const { valid, mode, submit, submitting } = this.props
    let steps = []
    switch (mode) {
      case ACTIONS.UPLOAD_PDF:
        steps = this.getUploadPdfStep()
        break
      case ACTIONS.EDIT_TOOLS:
        steps = this.getEditTools()
        break
      default: // fallthrough
    }
    return (
      <StepHandler
        onFinalStepSubmit={submit}
        steps={steps}
        overview
        valid={valid}
        submitting={submitting}
      />
    )
  }
}

export const onSubmit = (values, dispatch, props) => {
  const {
    mode,
    item,
    closeModal,
    intl,
    updateLinks,
    updateDocumentAsFile,
    fetchDocument,
    currentLanguage,
  } = props

  let submission
  switch (mode) {
    case ACTIONS.UPLOAD_PDF:
      const documentAsFile = toRelatedDocument(values.pdfDocuments)
      submission = updateDocumentAsFile({
        opfDocument: {
          ...item,
          documentAsFile,
        },
      }).then(async () => await fetchDocument({ slug: getCurrentSlug(currentLanguage) }))
      break
    case ACTIONS.EDIT_TOOLS:
      const links = toMediaList(values.links, values.supportingDocuments)
      submission = updateLinks({ opfDocument: { ...item, links } }).then(
        async () => await fetchDocument({ slug: getCurrentSlug(currentLanguage) })
      )
      break
    default:
      submission = Promise.resolve()
      break
  }
  return submission.then(closeModal).then(() => {
    const message = intl?.formatMessage({ id: "message.savedChanges" })
    notification.success({ message })
  })
}

DocumentForm.propTypes = {
  mode: PropTypes.string,
  formValues: PropTypes.object,
  currentLanguage: PropTypes.string,
  closeModal: PropTypes.func,
  updateDocumentAsFile: PropTypes.func,
  uploadDocumentFile: PropTypes.func,
  fetchDocument: PropTypes.func,
  ...propTypes,
}
const form = "documentMeta"
const ConnectedDocumentForm = reduxForm({ form, onSubmit })(DocumentForm)
const mapStateToProps = bindSelectors({
  formValues: getFormValues(form),
  currentLanguage: selectors.localeSelectors.getCurrentLanguage,
})
const mapDispatchToProps = bindActions({
  closeModal: actions.uiActions.closeFormModal,
  updateDocumentAsFile: actions.documentsActions.updateDocumentAsFile,
  updateLinks: actions.documentsActions.updateLinks,
  uploadDocumentFile: actions.documentsActions.uploadDocumentFile,
  fetchDocument: actions.documentsActions.fetchDocument,
})
const IntlDocumentForm = injectIntl(ConnectedDocumentForm)
export default connect(mapStateToProps, mapDispatchToProps)(IntlDocumentForm)
